import { WPLink } from '@/components/types';
import { SupportedLanguages } from '@/types/globals';
import { supportedLanguages } from '@/configuration/languages';

// Map post types to language config keys
const getBasePathFromConfig = (
  locale: SupportedLanguages,
  type: 'page' | 'post' | 'event' | 'news' | 'case-study' | 'jobs',
): string => {
  const config = supportedLanguages[locale];
  if (!config) return locale === 'fr' ? 'fr' : '';

  switch (type) {
    case 'post':
      return config.blogBasePath;
    case 'event':
      return config.eventBasePath;
    case 'news':
      return config.newsBasePath;
    case 'case-study':
      return config.caseStudyBasePath;
    case 'jobs':
      return config.careersBasePath;
    case 'page':
      return locale === 'fr' ? 'fr' : '';
    default:
      return locale === 'fr' ? 'fr' : '';
  }
};

export const generateLink = (
  locale: SupportedLanguages,
  type: 'page' | 'post' | 'event' | 'news' | 'case-study' | 'jobs',
  slug: string,
) => {
  // Absolute URLs pass through
  if (slug && /^https?:\/\//i.test(slug)) {
    return slug;
  }

  // Handle empty/undefined slugs
  if (!slug) {
    return locale === 'en' ? '/' : '/fr';
  }

  // Normalize slug (remove leading slashes)
  const normalizedSlug = slug.replace(/^\/+/, '');

  // Determine base path for locale+type (use same config as index pages)
  const basePath = getBasePathFromConfig(locale, type);

  // Avoid double-prefixing if slug is already localized or already has basePath
  const alreadyPrefixed = basePath
    ? normalizedSlug === basePath || normalizedSlug.startsWith(`${basePath}/`)
    : false;

  const path = alreadyPrefixed
    ? normalizedSlug
    : basePath
      ? `${basePath}/${normalizedSlug}`
      : normalizedSlug;

  return `/${path}`;
};

export const getLinkTarget = (
  locale: SupportedLanguages,
  link?: WPLink['target'],
) => {
  const data = link?.[0];
  if (!data) {
    return null;
  }
  return generateLink(locale, data.post_type, data.post_name);
};
